import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { QueryParameters } from '@/api/types/index';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

/**
 * Converts a date/time into a formatted string representation.
 * @param date The date/time.
 * @param format The output format pattern.
 * @returns A string representation of the date/time in the requested format.
 */
export function toDateFormatted(date: Date, format: string): string {
  return dayjs(date).format(format);
}

/**
 * Converts a UTC date/time into a formatted local date/time.
 * @param date The date/time in UTC.
 * @param format The output format pattern.
 * @returns A string representation of the date/time in the requested format.
 */
export function toLocalDateFormatted(date: Date, format: string): string {
  if (!date) return 'N/A';

  return dayjs.utc(date).local().format(format);
}

/**
 * Converts a date/time into a ISO 8601 string representation.
 * @param date The date/time.
 * @returns A string representation of the date/time in ISO 8601 format.
 */
export function toDateIsoString(date: Date): string {
  return dayjs(date).toISOString();
}

/**
 * Converts the begining of the day of a local date/time into a UTC date/time.
 * @param date The date/time in local timezone.
 * @returns A string representation of the date/time in UTC for the begining of `date`.
 */
export function startOfDay(date: Date): string {
  return dayjs(date).startOf('day').utc().format();
}

/**
 * Converts the end of the day of a local date/time into a UTC date/time.
 * @param date The date/time in local timezone.
 * @returns A string representation of the date/time in UTC for the end of `date`.
 */
export function endOfDay(date: Date): string {
  return dayjs(date).endOf('day').utc().format();
}

// TODO: Refactor to only include date related functionality
export function setFilterOptionDateByName(name: string): QueryParameters {
  const filterOptions: QueryParameters = {
    startDateTimeUtc: '',
    endDateTimeUtc: '',
    searchTerm: null
  };
  switch (name) {
    case 'Today':
      filterOptions.startDateTimeUtc = dayjs().toISOString();
      filterOptions.endDateTimeUtc = dayjs().toISOString();
      break;
    case 'Yesterday':
      filterOptions.startDateTimeUtc = dayjs().subtract(1, 'day').toISOString();
      filterOptions.endDateTimeUtc = dayjs().subtract(1, 'day').toISOString();
      break;
    case 'Last 7 days':
      filterOptions.startDateTimeUtc = dayjs().subtract(6, 'day').toISOString();
      filterOptions.endDateTimeUtc = dayjs().toISOString();
      break;
  }
  return filterOptions;
}

/**
 * Converts the end of the day of a local date/time into a UTC date/time.
 * @param dateFrom The date/time in local timezone of From date picker.
 * @param dateTo The date/time in local timezone of To date picker.
 * @param validDayRange The validation date range in days.
 * @returns A boolean represents the dateTo value and dateFrom range is valid or not.
 */
export function checkValidDateRange(dateFrom: string, dateTo: string, validDayRange: number): boolean {
  const dateRange = dayjs(dateTo).diff(dateFrom, 'day').toString();
  return parseInt(dateRange) < validDayRange;
}
export default { toDateFormatted, toLocalDateFormatted, toDateIsoString, startOfDay, endOfDay };
